import { useMutation } from '@apollo/client';
import { ButtonPrimary, Flex, Spinner } from 'components';
import {
  ADD_ATTACHMENTS_TO_CASE,
  ADD_COMMENT_TO_CASE,
} from 'connectors/mutations/case';
import React, { useCallback, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { caseAssetsState, notificationState } from 'state/atoms';
import {
  MAXIMUM_ATTCHMENT_SIZE,
  MAXIMUM_NO_OF_ATTACHMENTS_PER_COMMENT,
} from 'utils/constants';
import { useNotifyError } from 'utils/hooks';
import {
  AttachInput,
  AttachLink,
  CommentForm,
  CommentInput,
  ErrorMessagePlaceholder,
  RemoveFileLink,
  SelectedFileName,
  SelectedFiles,
  SpinnerWrapper,
  SubmitWrapper,
} from './styledComponents';
import {
  FormErrorMessage,
  FormErrorMessageOnTop,
} from 'components/form/FormErrorMessage';
import { readFilesAsBase64 } from 'utils/helpers/file';
import { splitFilesBySize } from './helpers';

const FILE_ATTACHMENT_INPUT = 'file-attachment-input';
const REQUIRED_INPUT_MESSAGE =
  'Please, provide a comment or attach files to proceed with the submission';
const MAX_NO_OF_FILES_PER_COMMENT_MESSAGE =
  'Too many files attached. Maximum of 5 files allowed per comment.';

const validateAttachments = (files) => {
  const result = { invalidFiles: [], validFiles: [] };

  for (const file of files) {
    if (file.size < MAXIMUM_ATTCHMENT_SIZE) {
      result.validFiles.push(file);
    } else {
      result.invalidFiles.push(file);
    }
  }

  return result;
};

const CommentSender = ({ caseId }) => {
  const [fileErrors, setFileErrors] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [assets, setAssets] = useRecoilState(caseAssetsState);
  const setNotification = useSetRecoilState(notificationState);
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);

  const onCommentCreated = useCallback(
    ({ comment, attachments = [], relationships = [] }) => {
      setNotification({
        show: true,
        text: 'Your comment was added to the case.',
        timeout: 4000,
      });
      setErrorMessage('');
      setComment('');
      setFiles([]);
      setFileErrors([]);
      setAssets({
        ...assets,
        attachments: [...attachments, ...assets.attachments],
        comments: [{ ...comment }, ...assets.comments],
        relationships: [...relationships, ...assets.relationships],
      });
    },
    [assets]
  );

  const notifyError = useNotifyError({
    fallbackMessage: 'Unable to submit the comment.',
    timeout: 4000,
  });

  const [sendComment, commentOptions] = useMutation(ADD_COMMENT_TO_CASE, {
    onError: notifyError,
  });

  const [
    linkAttachments,
    attachmentOptions,
  ] = useMutation(ADD_ATTACHMENTS_TO_CASE, { onError: notifyError });

  const handleKeyDown = useCallback(
    (event) => {
      if (event.keyCode === 13 && event.metaKey) {
        submitComment();
      }
    },
    [comment]
  );

  const onFileRemove = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }

    const fileInput = document.getElementById(FILE_ATTACHMENT_INPUT);

    if (fileInput) {
      fileInput.value = '';
    }

    setFiles([]);
  }, []);

  const onFileSelected = useCallback((event) => {
    const selectedFiles = event.currentTarget.files;
    const { invalidFiles, validFiles } = validateAttachments(selectedFiles);

    if (invalidFiles.length) {
      setFileErrors(invalidFiles);
      onFileRemove();

      return;
    } else if (validFiles.length > MAXIMUM_NO_OF_ATTACHMENTS_PER_COMMENT) {
      setErrorMessage(MAX_NO_OF_FILES_PER_COMMENT_MESSAGE);
      onFileRemove();

      return;
    } else {
      setErrorMessage('');
      setFileErrors([]);
    }

    setFiles(validFiles);
  }, []);

  const submitComment = useCallback(
    async (event) => {
      if (event) {
        event.preventDefault();
      }

      if (!comment && !files.length) {
        setErrorMessage(REQUIRED_INPUT_MESSAGE);
        return;
      }

      setErrorMessage('');
      setFileErrors([]);

      const newComment = !comment ? 'Attached files.' : comment.slice(0);
      const encodedFiles = await readFilesAsBase64(files);
      // Since the maximum payload size we can have is 5MB,
      // we have to divide the files to avoid AppSync crash
      const allFiles = splitFilesBySize(encodedFiles);

      const [attachments, ...additionalAttachments] = allFiles;

      const sendCommentResponse = await sendComment({
        variables: {
          input: {
            caseId,
            attachments,
            comment: newComment,
          },
        },
      });

      // If we don't have a response, an error happened and will be
      // captured through "handleApiError" method
      if (!sendCommentResponse) {
        return;
      }

      const response = sendCommentResponse.data.addCommentToCase;
      const createdComment = response.comment;
      let createdAttachments = response.attachments || [];
      let createdRelationships = [];

      if (additionalAttachments.length) {
        const linkRequests = additionalAttachments.map((attachments) =>
          linkAttachments({
            variables: {
              input: {
                caseId,
                attachments,
                commentId: createdComment.id,
              },
            },
          })
        );

        const linkResults = await Promise.all(linkRequests);

        // Include the additional attachments to the list
        linkResults.forEach((mutationResult) => {
          createdAttachments = [
            ...mutationResult.data.addAttachmentsToCase,
            ...createdAttachments,
          ];
        });
      }

      // Manually create the relationships that will link
      // comments and attachments
      if (createdAttachments.length) {
        createdRelationships = createdAttachments.map((attachment) => ({
          attachmentId: attachment.id,
          commentId: createdComment.id,
        }));
      }

      // Update the UI
      onCommentCreated({
        comment: createdComment,
        attachments: createdAttachments,
        relationships: createdRelationships,
      });
    },
    [caseId, comment, files]
  );

  if (commentOptions.loading || attachmentOptions.loading) {
    return (
      <SpinnerWrapper spacing="center">
        <Spinner />
      </SpinnerWrapper>
    );
  }

  return (
    <Flex direction="column">
      <CommentForm onSubmit={submitComment}>
        {errorMessage ? (
          <FormErrorMessageOnTop>{errorMessage}</FormErrorMessageOnTop>
        ) : (
          <ErrorMessagePlaceholder />
        )}
        <CommentInput
          // Ensure Grammarly doesn't act upon the field
          data-gramm="false"
          rows="3"
          cols="8"
          tabIndex="1"
          value={comment}
          className={errorMessage ? 'has-error' : ''}
          onKeyDown={handleKeyDown}
          onChange={(event) => setComment(event.currentTarget.value)}
        />
        <Flex spacing="space-between" alignment="flex-start">
          <Flex alignment="flex-start">
            <AttachLink htmlFor={FILE_ATTACHMENT_INPUT}>
              Attach Files (3.5MB each)
              <AttachInput
                id={FILE_ATTACHMENT_INPUT}
                type="file"
                onChange={onFileSelected}
                multiple
              />
            </AttachLink>
            {files.length ? (
              <SelectedFiles>
                {files.map((file, index) => (
                  <React.Fragment key={index}>
                    <SelectedFileName>{file.name}</SelectedFileName>
                    {index + 1 < files.length ? ', ' : null}
                  </React.Fragment>
                ))}
                <div>
                  <RemoveFileLink href="#" onClick={onFileRemove}>
                    Remove Selected Files
                  </RemoveFileLink>
                </div>
              </SelectedFiles>
            ) : null}
          </Flex>
          <SubmitWrapper>
            <ButtonPrimary tabIndex="2" type="submit">
              Submit
            </ButtonPrimary>
          </SubmitWrapper>
        </Flex>
        {fileErrors.length ? (
          <Flex direction="column">
            <FormErrorMessage>
              Maximum file size is 3.5MB. Please, remove the following files to
              proceed:
            </FormErrorMessage>
            <ul>
              {fileErrors.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          </Flex>
        ) : null}
      </CommentForm>
    </Flex>
  );
};

export default CommentSender;
